import { useEffect, useRef } from 'react';

type IonContentEl = HTMLElement & {
  getScrollElement?: () => Promise<HTMLElement>;
};

/** Sidebar / inset layout peers that change size without resizing the target. */
const LAYOUT_PEER_SELECTORS = [
  '[data-slot="sidebar-inset"]',
  '[data-sidebar="sidebar"]',
  '.group\\/sidebar-wrapper',
] as const;

/**
 * Follow CSS transitions (sidebar open/close) for one frame budget so the tip
 * does not lag one width behind the animated content.
 */
const LAYOUT_FOLLOW_MS = 420;

function collectLayoutObserveTargets(element: HTMLElement): HTMLElement[] {
  const seen = new Set<HTMLElement>();
  const out: HTMLElement[] = [];

  const add = (node: HTMLElement | null | undefined) => {
    if (!node || seen.has(node)) return;
    seen.add(node);
    out.push(node);
  };

  add(element);
  let parent: HTMLElement | null = element.parentElement;
  let depth = 0;
  while (parent && depth < 16) {
    add(parent);
    if (parent === document.body || parent === document.documentElement) break;
    parent = parent.parentElement;
    depth += 1;
  }

  for (const selector of LAYOUT_PEER_SELECTORS) {
    const peer = document.querySelector(selector);
    if (peer instanceof HTMLElement) add(peer);
  }

  add(document.documentElement);
  add(document.body);

  return out;
}

/**
 * Keep a viewport rect in sync while Ionic scrolls (shadow `.inner-scroll`)
 * and while shell chrome reflows (sidebar toggle / inset resize).
 *
 * TourKit's useElementPosition listens on window + light-DOM scroll parents;
 * ion-content often scrolls inside shadow DOM, so the hotspot goes stale.
 * ResizeObserver on the target alone also misses sidebar toggles: the CTA
 * translates / grows while its own box size is unchanged.
 *
 * Imperative only — mutate DOM from `onRect` without React re-renders.
 */
export function useLiveTargetRect(
  element: HTMLElement | null | undefined,
  onRect: (rect: DOMRect) => void,
): void {
  const onRectRef = useRef(onRect);
  useEffect(() => {
    onRectRef.current = onRect;
  }, [onRect]);

  useEffect(() => {
    if (!element?.isConnected) {
      return;
    }

    let cancelled = false;
    let scrollEl: HTMLElement | null = null;
    let raf = 0;
    let followRaf = 0;
    let followUntil = 0;
    let lastKey = '';

    const measure = () => {
      if (cancelled || !element.isConnected) return;
      const next = element.getBoundingClientRect();
      const key = `${next.top | 0},${next.left | 0},${next.width | 0},${next.height | 0}`;
      if (key === lastKey) return;
      lastKey = key;
      onRectRef.current(next);
    };

    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    const followTick = (now: number) => {
      followRaf = 0;
      measure();
      if (cancelled || now >= followUntil) return;
      followRaf = requestAnimationFrame(followTick);
    };

    const followLayout = () => {
      followUntil = performance.now() + LAYOUT_FOLLOW_MS;
      schedule();
      if (!followRaf) {
        followRaf = requestAnimationFrame(followTick);
      }
    };

    schedule();

    const ro = new ResizeObserver(followLayout);
    for (const node of collectLayoutObserveTargets(element)) {
      ro.observe(node);
    }

    window.addEventListener('scroll', schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', followLayout, { passive: true });
    window.visualViewport?.addEventListener('resize', followLayout);
    window.visualViewport?.addEventListener('scroll', schedule);

    // Sidebar width animates via CSS transition — size observers fire at the
    // start/end, but mid-tween frames need the follow loop above.
    document.addEventListener('transitionrun', followLayout, true);
    document.addEventListener('transitionend', followLayout, true);

    const ionContent = element.closest('ion-content') as IonContentEl | null;
    ionContent?.addEventListener('ionScroll', schedule);

    void ionContent?.getScrollElement?.().then((el) => {
      if (cancelled || !el) return;
      scrollEl = el;
      scrollEl.addEventListener('scroll', schedule, { passive: true });
    });

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (followRaf) cancelAnimationFrame(followRaf);
      ro.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', followLayout);
      window.visualViewport?.removeEventListener('resize', followLayout);
      window.visualViewport?.removeEventListener('scroll', schedule);
      document.removeEventListener('transitionrun', followLayout, true);
      document.removeEventListener('transitionend', followLayout, true);
      ionContent?.removeEventListener('ionScroll', schedule);
      scrollEl?.removeEventListener('scroll', schedule);
    };
  }, [element]);
}
